import {
    Button,
    Typography,
    useTheme,
    styled,
    Box,
} from "@mui/material";
import { SimpleDepositEntity } from "api/generated";
import { FC, useEffect, useState } from "react";
import { t } from "i18next";
import AppModal from "components/AppModal";
import CalendarInput from "components/input-fields/CalendarInput";
import { depositsApi } from "api";
import { useFormik } from "formik";
import CurrencyInput from "react-currency-input-field";
import FlexBox from "components/flexbox/FlexBox";
import { ignoreUTC } from "utils/utils";
import * as Yup from 'yup'

interface ModalProps {
    open: boolean;
    onClose: () => void;
    defaultDate?: Date;
    fetchTableData: () => void;
}


const StyledAppModal = styled(AppModal)(({ theme }) => ({
    width: 500,
    maxHeight: 700,
    overflowY: "auto",
    [theme.breakpoints.down("sm")]: {
        width: "100%",
        maxHeight: "80%",
    },
}));

const moneyFormatter = new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

const SimplifiedAddDepositModal: FC<ModalProps> = ({
    open,
    onClose,
    defaultDate,
    fetchTableData,
}) => {
    const theme = useTheme();
    const [date, setDate] = useState(defaultDate ?? new Date);
    const [isDateValid, setIsDateValid] = useState(true);
    const [deposit, setDeposit] = useState<SimpleDepositEntity>();
    const [depositable, setDepositable] = useState(0);
    const [stringValue, setStringValue] = useState("0");

    const fetchData = async () => {
        if (!date)
            return;

        depositsApi.findOneByDate(+date).then(({ data }) => {
            setDeposit(data);
            setStringValue(data?.value?.toString() ?? "0")
        });

        depositsApi.getdDateDepositable(+date).then(({ data }) => {
            setDepositable(data);
        })

    };

    useEffect(() => {
        fetchData();
    }, [date]);

    const schema = Yup.object({
        value: Yup.number().required().min(0)
    })


    const formik = useFormik({
        initialValues: {
            value: deposit?.value ?? 0
        },
        validationSchema: schema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            if (!date) return;
            if (deposit) {
                await depositsApi.update(deposit.id.toString(), values)
            }
            else {
                await depositsApi.create({
                    date: ignoreUTC(date).toISOString(),
                    value: values.value
                });
            }
            fetchTableData();
            onClose();
        },
    });

    const handleCancel = () => {
        formik.resetForm();
        onClose();
    };

    const resetValues = () => {
        setDate(defaultDate ?? new Date());
        setStringValue(deposit?.value?.toString() ?? "0");
    }

    useEffect(() => {
        resetValues();
    }, [open])

    const { values } = formik;


    const isOver = values.value > depositable;

    return (
        <StyledAppModal open={open} handleClose={handleCancel} alignContent="center" alignItems="center">
            <Box p={2}>
                <Typography variant="h5" sx={{ mb: 3 }}>
                    {!deposit ? t("deposit.add") : t("deposit.update")}
                </Typography>

                <CalendarInput
                    sx={{ mb: 3 }}
                    format="dd.MM.yyyy"
                    label={t("deposit.date")}
                    value={date}
                    onChange={(newValue) => {
                        if (newValue && newValue instanceof Date && !isNaN(+newValue)) {
                            setDate(newValue);
                            setIsDateValid(true);
                        } else {
                            setIsDateValid(false);
                        }
                    }}
                    slotProps={{
                        textField: {
                            helperText: !isDateValid && t("date.error"),
                            error: !isDateValid,
                        },
                    }}
                />
                <CurrencyInput
                    style={{
                        width: "100%",
                        padding: "8px",
                        borderRadius: 4,
                        fontSize: 16,
                        border: `1px solid ${isOver || values.value < 0 ?
                            theme.palette.error.main : theme.palette.grey[400]
                            }`,
                    }}
                    value={stringValue}
                    decimalsLimit={2}
                    onValueChange={(val, _name, values) => {
                        setStringValue(val ?? "");
                        formik.setFieldValue('value', values?.float)
                    }
                    }
                    intlConfig={{ locale: "de-DE", currency: "EUR" }}
                />
                {
                    formik.errors.value ?
                        <Typography
                            variant="body2"
                            sx={{
                                mt: 1,
                                color: values.value < 0 ? theme.palette.error.main : theme.palette.text.secondary,
                            }}
                        >
                            {t('value.notValid')}

                        </Typography> : <></>

                }


                <Typography
                    variant="body2"
                    sx={{
                        mt: 1,
                        color: isOver ? theme.palette.error.main : theme.palette.text.secondary,
                    }}
                >

                    {t("deposit.toBePaid")}{" "}
                    {moneyFormatter.format(
                        depositable
                    )}
                </Typography>



                <FlexBox justifyContent="space-between" gap={2} sx={{ mt: 3 }}>
                    <Button variant="contained" color="error" onClick={handleCancel}>
                        {t("cancel")}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            formik.handleSubmit();
                        }}
                        disabled={!!formik.errors.value}
                    >
                        {!deposit ? t("add") : t("save")}
                    </Button>
                </FlexBox>
            </Box>
        </StyledAppModal>
    );
};

export default SimplifiedAddDepositModal;